import type { ServerStatus } from '../services/backend';
import { CardSkeleton } from './Skeleton';
import {
  ServerIcon,
  BeakerIcon,
  BoltIcon,
  ChartBarIcon,
  CircleStackIcon,
  GlobeAltIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentIcon,
  CloudIcon,
  FolderIcon,
  TagIcon,
  CommandLineIcon,
  RectangleStackIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';
import { PageHeader } from './PageHeader';
import { ServerStatusAdmin } from './ServerStatusAdmin';
import { ServerStatusExtraction } from './ServerStatusExtraction';

interface ServerStatusProps {
  status: ServerStatus | null;
  loading: boolean;
  error: string | null;
  className?: string;
}

function StatusBadge({ enabled }: { enabled: boolean }) {
  return (
    <div className="flex items-center gap-1.5">
      {enabled ? (
        <CheckCircleIcon className="h-4 w-4 text-green-500" />
      ) : (
        <XCircleIcon className="h-4 w-4 text-gray-400" />
      )}
      <span
        className={`text-sm font-medium ${enabled ? 'text-green-700 dark:text-green-400' : 'text-gray-500 dark:text-zinc-500'}`}
      >
        {enabled ? 'On' : 'Off'}
      </span>
    </div>
  );
}

function StatCard({
  icon: Icon,
  title,
  value,
  className = '',
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  value: number;
  className?: string;
}) {
  return (
    <div
      className={`rounded-lg border border-gray-100 bg-white p-4 shadow-sm dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100 ${className}`}
    >
      <div className="flex items-center gap-3">
        <div className="rounded-lg bg-indigo-50 p-2 dark:bg-indigo-900/50">
          <Icon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
        </div>
        <div className="min-w-0">
          <h3 className="text-sm font-medium text-gray-500 dark:text-zinc-400">{title}</h3>
          <p className="mt-1 text-2xl font-semibold tabular-nums text-gray-900 dark:text-zinc-100">
            {value.toLocaleString()}
          </p>
        </div>
      </div>
    </div>
  );
}

function FeatureRow({
  icon: Icon,
  label,
  enabled,
  detail,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  enabled: boolean;
  detail?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <div className="flex items-center gap-2">
        <Icon className="h-5 w-5 shrink-0 text-gray-400 dark:text-zinc-500" />
        <span className="text-sm font-medium text-gray-700 dark:text-zinc-300">{label}</span>
      </div>
      <div className="flex flex-col items-start gap-1 sm:items-end">
        <StatusBadge enabled={enabled} />
        {detail ? (
          <span className="max-w-full break-all text-left text-xs text-gray-500 dark:text-zinc-500 sm:text-right">
            {detail}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function DefListRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:justify-between">
      <dt className="text-sm font-medium text-gray-500 dark:text-zinc-400">{label}</dt>
      <dd className="break-all text-sm font-medium text-gray-900 dark:text-zinc-100 sm:text-right">{children}</dd>
    </div>
  );
}

export function ServerStatus({ status, loading, error, className = '' }: ServerStatusProps) {
  const header = (
    <PageHeader
      title="Server status"
      description="Deployment build, feature flags, and live database counts for this environment."
      icon={<ServerIcon className="size-6" />}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-600 dark:text-zinc-400">
        <span>
          Build <span className="font-medium text-gray-900 dark:text-zinc-200">{status?.version}</span>
        </span>
        {status?.build?.date ? (
          <span className="hidden sm:inline" aria-hidden>
            ·
          </span>
        ) : null}
        {status?.build?.date ? <span title={`Build id: ${status.build.id}`}>{status.build.date}</span> : null}
        <span className="hidden sm:inline" aria-hidden>
          ·
        </span>
        <span>
          Runtime <span className="font-medium text-gray-900 dark:text-zinc-200">{status?.environment}</span>
          {status?.configProfile && status.configProfile !== status.environment ? (
            <span className="text-gray-500"> · profile {status.configProfile}</span>
          ) : null}
        </span>
      </div>
    </PageHeader>
  );

  if (loading) {
    return (
      <>
        {header}
        <CardSkeleton className={className} />
      </>
    );
  }

  if (error || !status) {
    return (
      <div className={`rounded-lg bg-white p-6 shadow dark:bg-zinc-800 ${className}`}>
        <div className="text-center text-red-600 dark:text-red-400">{error || 'Failed to load server status'}</div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {header}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <StatCard icon={ChartBarIcon} title="Categories" value={status.stats.categories} />
        <StatCard icon={BuildingOfficeIcon} title="Organisations" value={status.stats.organisations} />
        <StatCard icon={FolderIcon} title="Projects" value={status.stats.projects} />
        <StatCard icon={DocumentIcon} title="Documents" value={status.stats.documents} />
        <StatCard icon={RectangleStackIcon} title="Pages" value={status.stats.pages} />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-lg border border-gray-100 bg-white p-6 shadow-sm dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100">
          <div className="mb-4 flex items-center gap-2">
            <BoltIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-zinc-100">Features</h3>
          </div>
          <div className="space-y-4">
            <FeatureRow
              icon={DocumentIcon}
              label="LLM page parser"
              enabled={status.features.llmPageParser.enabled}
              detail={
                <span className="font-mono text-[11px] leading-snug sm:text-xs">
                  {status.features.llmPageParser.model} — {status.features.llmPageParser.baseUrl}
                </span>
              }
            />
            <FeatureRow icon={TagIcon} label="Topics (project topics)" enabled={status.features.topics} />
            <FeatureRow
              icon={CommandLineIcon}
              label="Bridge Python scripts"
              enabled={status.features.bridgePythonScripts}
            />
            <FeatureRow icon={BeakerIcon} label="Category training" enabled={status.features.training} />
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-lg border border-gray-100 bg-white p-6 shadow-sm dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100">
            <div className="mb-4 flex items-center gap-2">
              <GlobeAltIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-zinc-100">Embeddings</h3>
            </div>
            <dl className="space-y-3">
              <DefListRow label="Provider">{status.embeddings.provider}</DefListRow>
              <DefListRow label="Model">{status.embeddings.model ?? '—'}</DefListRow>
              {status.embeddings.host ? <DefListRow label="Host">{status.embeddings.host}</DefListRow> : null}
            </dl>
          </div>

          <ServerStatusExtraction extraction={status.extraction} />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-lg border border-gray-100 bg-white p-6 shadow-sm dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100">
          <div className="mb-4 flex items-center gap-2">
            <CloudIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-zinc-100">Google Cloud</h3>
          </div>
          <dl className="space-y-3">
            <DefListRow label="Project">{status.cloud.projectId}</DefListRow>
            <DefListRow label="Gemini / Vertex region">{status.cloud.geminiVertexLocation}</DefListRow>
            <DefListRow label="Gemini client mode">
              <span className="font-mono text-xs">{status.cloud.geminiClientMode}</span>
            </DefListRow>
            <DefListRow label="Bucket (documents)">
              <span className="font-mono text-xs">{status.cloud.buckets.documents}</span>
            </DefListRow>
            <DefListRow label="Bucket (pages)">
              <span className="font-mono text-xs">{status.cloud.buckets.pages}</span>
            </DefListRow>
            <DefListRow label="Bucket (reports)">
              <span className="font-mono text-xs">{status.cloud.buckets.reports}</span>
            </DefListRow>
          </dl>
        </div>

        <div className="rounded-lg border border-gray-100 bg-white p-6 shadow-sm dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100">
          <div className="mb-4 flex items-center gap-2">
            <CircleStackIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-zinc-100">Runtime &amp; database</h3>
          </div>
          <dl className="space-y-3">
            <DefListRow label="HTTP/2">{status.server.useHttp2 ? 'Enabled' : 'Disabled'}</DefListRow>
            <DefListRow label="Request timeout">
              {status.server.requestTimeoutMs >= 60000
                ? `${Math.round(status.server.requestTimeoutMs / 60000)} min`
                : `${status.server.requestTimeoutMs} ms`}
            </DefListRow>
            <DefListRow label="PostgreSQL">
              <span className="font-mono text-xs">{status.postgres.uri}</span>
            </DefListRow>
          </dl>
        </div>
      </div>

      <ServerStatusAdmin />
    </div>
  );
}
